import { attitudeOAs, contentOAs, curriculum6, difficultyOptions, questionTypeOptions, skillOAs, sourceTypeOptions, statusOptions, stimulusTypeOptions, supportOptions, vocabularyOptions } from '../data/curriculum'
import type { ChoiceOption, DataBundle, DynamicChoice, Row } from '../types'
import { normalizeText, parseList } from './csv'

function uniqueOptions(values: Array<string | ChoiceOption>): ChoiceOption[] {
  const map = new Map<string, ChoiceOption>()
  values.forEach((value) => {
    const option = typeof value === 'string' ? { value, label: value } : value
    if (option.value && !map.has(normalizeText(option.value))) map.set(normalizeText(option.value), option)
  })
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'))
}

function topicsRows(data: DataBundle): Row[] { return data.files['topics.csv'] ?? [] }

export function buildChoices(kind: DynamicChoice, data: DataBundle, row: Row = {}): ChoiceOption[] {
  const files = Object.values(data.files).flat()
  const selectedUnit = normalizeText(row.unit || '')
  const selectedOAs = parseList(row.oa || row.selected_oas || '').map(normalizeText)
  switch (kind) {
    case 'units':
      return uniqueOptions([
        ...topicsRows(data).map((item) => ({ value: item.unit || `Unidad ${item.class_number || ''}`.trim(), label: `${item.unit_code ? `${item.unit_code} · ` : ''}${item.unit || `Unidad ${item.class_number || ''}`}`.trim(), description: item.semester || item.summary })),
        ...files.map((item) => item.unit),
        ...data.sessions.map((item) => item.unit),
      ].filter(Boolean) as Array<string | ChoiceOption>)
    case 'oas': {
      const unitRows = selectedUnit ? topicsRows(data).filter((item) => normalizeText(item.unit || '') === selectedUnit) : []
      const associated = new Set(unitRows.flatMap((item) => parseList(item.oa || '')).filter((code) => /^OA\s*\d+/i.test(code)).map(normalizeText))
      const available = associated.size ? contentOAs.filter((item) => associated.has(normalizeText(item.shortCode))) : contentOAs
      return available.map((item) => ({ value: item.shortCode, label: `${item.shortCode} · ${item.text}`, description: item.axis, group: item.axis, disabled: !item.active }))
    }
    case 'skills': {
      const base = row.oa ? suggestedSkillsForOA(row.oa) : skillOAs.map((item) => ({ value: item.shortCode, label: `${item.shortCode} · ${item.text}`, description: item.axis, group: item.axis }))
      const typeHints: Record<string, string[]> = {
        multiple_choice: ['OAH e','OAH g','OAH i','OAH j'],
        true_false: ['OAH e','OAH i'],
        ordering: ['OAH a','OAH b','OAH c'],
        matching: ['OAH b','OAH e','OAH g'],
        source: ['OAH e','OAH g','OAH i','OAH j'],
        development: ['OAH h','OAH i','OAH l','OAH n'],
      }
      const preferred = new Set(typeHints[row.type || ''] || [])
      return [...base].sort((a, b) => Number(preferred.has(b.value)) - Number(preferred.has(a.value)))
    }
    case 'attitudes': return attitudeOAs.map((item) => ({ value: item.shortCode, label: `${item.shortCode} · ${item.text}`, description: item.axis }))
    case 'topics': {
      const filtered = topicsRows(data).filter((item) => {
        const unitOk = !selectedUnit || normalizeText(item.unit || '') === selectedUnit
        const oas = parseList(item.oa || '').map(normalizeText)
        const oaOk = !selectedOAs.length || !oas.length || selectedOAs.some((oa) => oas.includes(oa))
        return unitOk && oaOk
      })
      return uniqueOptions(filtered.map((item) => ({ value: item.topic, label: item.topic, description: item.summary || item.unit })))
    }
    case 'subtopics': {
      const filtered = topicsRows(data).filter((item) => !row.topic || normalizeText(item.topic || item.parent_topic || '') === normalizeText(row.topic))
      return uniqueOptions(filtered.map((item) => item.subtopic).filter(Boolean))
    }
    case 'sources': return uniqueOptions((data.files['sources.csv'] ?? []).filter((item) => !selectedUnit || normalizeText(item.unit || '') === selectedUnit).filter((item) => !selectedOAs.length || !item.oa || selectedOAs.includes(normalizeText(item.oa))).filter((item) => !row.topic || !item.topic || normalizeText(item.topic) === normalizeText(row.topic)).map((item) => { const uses = Object.values(data.files).flat().filter((content) => content.source_id === item.id).length; return ({ value: item.id, label: `${item.id} · ${item.title}`, description: [item.author, item.date, item.source_type, `${uses} uso(s)`].filter(Boolean).join(' · ') }) }))
    case 'materials': return uniqueOptions((data.files['study_support.csv'] ?? []).filter((item) => !selectedUnit || normalizeText(item.unit || '') === selectedUnit).filter((item) => !selectedOAs.length || !item.oa || selectedOAs.includes(normalizeText(item.oa))).filter((item) => !row.topic || !item.topic || normalizeText(item.topic) === normalizeText(row.topic)).map((item) => ({ value: item.id, label: `${item.id} · ${item.title}`, description: [item.topic, item.level].filter(Boolean).join(' · ') })))
    case 'sessions': return uniqueOptions(data.sessions.map((item) => ({ value: item.id, label: `${item.id} · ${item.title}`, description: item.unit })))
    case 'courses': return ['6°A','6°B','6°C','6°D'].map((value) => ({ value, label: value }))
    case 'difficulty': return difficultyOptions
    case 'vocabulary': return vocabularyOptions
    case 'support': return supportOptions
    case 'status': return statusOptions.map((value) => ({ value, label: value }))
    case 'sourceTypes': return sourceTypeOptions.map((value) => ({ value, label: value }))
    case 'stimulusTypes': return stimulusTypeOptions.map((value) => ({ value, label: value }))
    case 'imageStatuses': return ['Pendiente de subir a GitHub','Subida a GitHub','Publicada y vinculada'].map((value) => ({ value, label: value }))
    case 'images': return uniqueOptions(data.images.filter((image) => !row.unit || image.unit === row.unit).filter((image) => !row.topic || image.topic === row.topic).map((image) => ({ value: image.publicPath, label: `${image.fileName} · ${image.status}`, description: image.altText })))
    case 'questionTypes': return questionTypeOptions
    default: return []
  }
}

export function curriculumText(shortCode: string): string {
  return curriculum6.find((item) => item.shortCode === shortCode)?.text ?? ''
}

export function suggestedSkillsForOA(oa: string): ChoiceOption[] {
  const number = Number(oa.match(/\d+/)?.[0] ?? 0)
  const codes = number <= 9
    ? ['OAH a','OAH b','OAH c','OAH e','OAH g','OAH h','OAH i','OAH j','OAH l','OAH n']
    : number <= 14
      ? ['OAH d','OAH f','OAH g','OAH h','OAH i','OAH k','OAH n']
      : ['OAH h','OAH i','OAH j','OAH k','OAH m','OAH n']
  return skillOAs.filter((item) => codes.includes(item.shortCode)).map((item) => ({ value: item.shortCode, label: `${item.shortCode} · ${item.text}`, description: item.axis }))
}

export { contentOAs }
